import {useEffect, useRef} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {
  createGuidedBundleSelector,
  getGuidedSealBaselineIds,
  isBoxChampEnabled,
  isGuidedVerificationOn
} from '../utils/boxChampGuidedSelectors';
import {
  loadBoxChampCatalog,
  refreshBoxChampBoxes
} from '../../../actions/boxChampGuidedActions';
import {releaseGuidedCompletionIfSettled} from '../../../actions/postScanActions';
import {handleReloadOrder} from '../../../actions/scanpackScanningActions';
import {setBoxChampState, setActiveBoxIndex} from '../../../actions/scanpackScanningSetters';
import {isSetAsideRejection, setAsideMessage} from '../../scanpack/BoxChampSetAsideNotice';
import {HANDOFF_IDLE} from '../../scanpack/boxChampHandoff';
import audioService from '../../../services/AudioService';

const selectGuidedBundle = createGuidedBundleSelector();

export const useBoxChampGuided = () => useSelector(selectGuidedBundle);

const selectBoxChamp = state => state.scanPackScanning.boxChamp || {};

const PLAN_REFRESH_ATTEMPTS = 3;
const PLAN_REFRESH_RETRY_MS = 1000;

export const useBoxChampGuidedEffects = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const {view, handoff, paused, orderPacked, activeBoxId} = useBoxChampGuided();
  const currentOrder = useSelector(state => state.scanPackScanning.currentOrder);
  const activeBoxIndex = useSelector(state => state.scanPackScanning.activeBoxIndex);
  const verificationOn = useSelector(isGuidedVerificationOn);
  const boxChampEnabled = useSelector(isBoxChampEnabled);
  const catalogLength = useSelector(state => selectBoxChamp(state).catalog?.length || 0);
  const baselineOrderId = useSelector(state => selectBoxChamp(state).sealBaseline?.orderId);
  const justConfirmedBoxId = useSelector(state => selectBoxChamp(state).justConfirmedBoxId);
  const completionPending = useSelector(state => Boolean(selectBoxChamp(state).completionPending));
  const setAsideText = useSelector(state => selectBoxChamp(state).setAsideMessage || '');
  const setAsideConfirmation = useSelector(state => Boolean(selectBoxChamp(state).setAsideConfirmation));
  const handoffMessage = useSelector(state => selectBoxChamp(state).handoffMessage || '');
  const logAckTime = useSelector(state => state.saveLog?.time);
  const planOpAckedAt = useSelector(state => selectBoxChamp(state).planOpAckedAt ?? null);

  const hasView = Boolean(view);
  const orderId = currentOrder?.id ?? null;
  const boxes = currentOrder?.boxes;

  useEffect(() => {
    if (catalogLength || orderId == null) return;
    if (verificationOn || boxChampEnabled || hasView) dispatch(loadBoxChampCatalog());
  }, [catalogLength, orderId, verificationOn, boxChampEnabled, hasView]);

  useEffect(() => {
    if (!view || orderId == null || baselineOrderId === orderId) return;
    dispatch(
      setBoxChampState({
        sealBaseline: {orderId, ids: getGuidedSealBaselineIds(store.getState(), view)}
      })
    );
  }, [view, orderId, baselineOrderId]);

  useEffect(() => {
    if (justConfirmedBoxId == null) return;
    const confirmed = (boxes || []).find(b => b && b.id === justConfirmedBoxId);
    if (confirmed && confirmed.completed_at) {
      dispatch(setBoxChampState({justConfirmedBoxId: null}));
    }
  }, [justConfirmedBoxId, boxes]);

  useEffect(() => {
    if (!view || activeBoxId == null) return;
    const index = (boxes || []).findIndex(b => b && b.id === activeBoxId);
    if (index >= 0 && index !== activeBoxIndex) dispatch(setActiveBoxIndex(index));
  }, [view, activeBoxId, boxes, activeBoxIndex]);

  useEffect(() => {
    if (completionPending && !paused && orderPacked) dispatch(releaseGuidedCompletionIfSettled());
  }, [completionPending, paused, orderPacked]);

  useEffect(() => {
    if (
      setAsideText &&
      !setAsideConfirmation &&
      setAsideText === handoffMessage &&
      handoff.kind !== HANDOFF_IDLE
    ) {
      dispatch(setBoxChampState({setAsideMessage: ''}));
    }
  }, [setAsideText, setAsideConfirmation, handoffMessage, handoff.kind]);

  const lastAck = useRef(logAckTime);
  useEffect(() => {
    if (logAckTime === lastAck.current) return;
    lastAck.current = logAckTime;
    const state = store.getState();
    if (!hasView) return;
    const guided = selectBoxChamp(state);
    const payload = state.saveLog?.logs;
    if (isSetAsideRejection(payload)) {
      const message = setAsideMessage(payload);
      if (message) {
        audioService.playSetAsideSound();
        dispatch(
          setBoxChampState({
            setAsideMessage: message,
            setAsideConfirmation: false,
            handoffDismissedKey: null
          })
        );
        dispatch(refreshBoxChampBoxes());
        dispatch(handleReloadOrder());
      }
    } else if (guided.setAsideMessage && !guided.setAsideConfirmation) {
      dispatch(setBoxChampState({setAsideMessage: ''}));
    }
  }, [logAckTime]);

  useEffect(() => {
    if (planOpAckedAt == null) return undefined;
    let cancelled = false;
    let retry = null;
    const attempt = triesLeft => {
      Promise.resolve(dispatch(refreshBoxChampBoxes())).then(result => {
        if (cancelled || !result || result.ok || triesLeft <= 1) return;
        retry = setTimeout(() => attempt(triesLeft - 1), PLAN_REFRESH_RETRY_MS);
      });
    };
    attempt(PLAN_REFRESH_ATTEMPTS);
    return () => {
      cancelled = true;
      clearTimeout(retry);
    };
  }, [planOpAckedAt]);
};

export default useBoxChampGuided;
